import { supabase } from '../supabase';
import { useMutation } from '@tanstack/react-query';
import { User, Warning } from '@shiftly/shared';

// User logging types
export type UserLogAction = 
  | 'CREATE'
  | 'UPDATE' 
  | 'DELETE'
  | 'DEACTIVATE'
  | 'REACTIVATE'
  | 'ADD_WARNING'
  | 'REMOVE_WARNING'
  | 'INVITE'
  | 'PROMOTE_TRIAL';

export interface UserLogData {
  action: UserLogAction;
  user_id: string;
  performed_by: string;
  details: string;
  changes?: {
    before?: any;
    after?: any;
  };
  metadata?: any;
}

const SupabaseUserLoggingService = {
  async logUserAction(logData: UserLogData): Promise<void> {
    try {
      let changesData: any = null;

      // For CREATE operations, use metadata as the "after" state
      if (logData.action === 'CREATE' && logData.metadata) {
        changesData = {
          after: logData.metadata
        };
      }
      // For UPDATE operations, use the provided changes
      else if (logData.changes) {
        changesData = logData.changes;
      }
      // For other operations, include metadata if available
      else if (logData.metadata) {
        changesData = {
          metadata: logData.metadata
        };
      }

      const { error } = await supabase
        .from('admin_actions')
        .insert({
          type: `${logData.action}_USER`,
          performed_by: logData.performed_by,
          target_id: logData.user_id,
          target_type: 'USER',
          details: logData.details,
          changes: changesData,
        });

      if (error) {
        console.error('Error logging user action:', error);
        throw error;
      }
    } catch (error) {
      console.error('Error in logUserAction:', error);
      throw error;
    }
  },

  // User logging methods
  async logUserCreate(user: User, performed_by: string): Promise<void> {
    await this.logUserAction({
      action: 'CREATE',
      user_id: user.id,
      performed_by: performed_by,
      details: `User created: ${user.full_name}`,
      metadata: user,
    });
  },

  async logUserUpdate(user_id: string, changes: { before: Partial<User>; after: Partial<User> }, performed_by: string): Promise<void> {
    await this.logUserAction({
      action: 'UPDATE',
      user_id: user_id,
      performed_by: performed_by,
      details: `User updated`,
      changes,
    });
  },

  async logUserDelete(user: User, performed_by: string): Promise<void> {
    await this.logUserAction({
      action: 'DELETE',
      user_id: user.id,
      performed_by: performed_by,
      details: `User deleted: ${user.full_name}`,
      changes: {
        before: user
      },
    });
  },

  async logUserDeactivate(user: User, performed_by: string): Promise<void> {
    await this.logUserAction({
      action: 'DEACTIVATE',
      user_id: user.id,
      performed_by: performed_by,
      details: `User deactivated: ${user.full_name}`,
      changes: {
        before: user
      },
    });
  },

  async logUserReactivate(user: User, performed_by: string): Promise<void> {
    await this.logUserAction({
      action: 'REACTIVATE',
      user_id: user.id,
      performed_by: performed_by,
      details: `User reactivated: ${user.full_name}`,
      changes: {
        before: user
      },
    });
  },

  async logUserAddWarning(user: User, warning: Warning, performed_by: string): Promise<void> {
    await this.logUserAction({
      action: 'ADD_WARNING',
      user_id: user.id,
      performed_by: performed_by,
      details: `Warning added to ${user.full_name}: ${warning.message}`,
      changes: {
        after: {
          user_id: user.id,
          warning: warning
      },
      },
    });
  },

  async logUserRemoveWarning(user: User, warning: Warning, performed_by: string): Promise<void> {
    await this.logUserAction({
      action: 'REMOVE_WARNING',
      user_id: user.id,
      performed_by: performed_by,
      details: `Warning removed from ${user.full_name}: ${warning.message}`,
      changes: {
        after: {
          user_id: user.id,
          warning: warning
      },
      },
    });
  },

  async logUserInvite(emails: string[], roles: string[], pay_classes: string[], performed_by: string): Promise<void> {
    await this.logUserAction({
      action: 'INVITE',
      user_id: '00000000-0000-0000-0000-000000000000',
      performed_by: performed_by,
      details: `Users invited: ${emails.join(', ')}`,
      changes: {
        after: {
        emails: emails,
          roles: roles,
          pay_classes: pay_classes
        }
      },
    });
  },

  async logUserPromoteTrial(user: User, email: string, roles: string[], performed_by: string): Promise<void> {
    await this.logUserAction({
      action: 'PROMOTE_TRIAL',
      user_id: user.id,
      performed_by: performed_by,
      details: `User promoted from trial: ${user.full_name}`,
      changes: {
        after: {
        user: user,
        email: email,
        roles: roles
        }
      },
    });
  },
};

// React Query hooks for logging
export const useLogUserAction = () => {
  return useMutation({
    mutationFn: (logData: UserLogData) => 
      SupabaseUserLoggingService.logUserAction(logData),
  });
};

export default SupabaseUserLoggingService;
